import { Button } from "@components/buttons/button";
import { Card } from "@components/cards/card";
import { ModalInfo } from "@components/modals/modal-info";
import { ModalPayoutRequest } from "@components/modals/modal-payout-request";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { useModalContext } from "@hooks/contexts/modal-context";
import { ICashDTO } from "@hooks/dtos/ICashDTO";
import {
  CashRaffleTicketReleaseQueueStatusEnum,
  ICashRaffleTicketReleaseQueueDTO,
  ProfitTicketBalanceReleaseTypeEnum,
} from "@hooks/dtos/ICashRaffleTicketReleaseQueueDTO";
import { ISummaryCashCustomerDTO } from "@hooks/dtos/IFinancialDTO";
import { IModalInfoDTO } from "@hooks/dtos/IModalInfoDTO";
import {
  cashGroups,
  cashOps,
  cashTypes,
  getCashFormattedValue,
  getCashGroup,
  getCashOp,
  getCashTypes,
} from "@hooks/functions/Cash";
import { Masks } from "@hooks/functions/Masks/Masks";
import { addHashClickAndCopyHtml } from "@hooks/functions/Misc/Html";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FaCheck, FaCheckSquare, FaClock, FaTicketAlt } from "react-icons/fa";
import {
  FaBroom,
  FaCircleInfo,
  FaExpand,
  FaMinus,
  FaMoneyBillTransfer,
  FaTicketSimple,
} from "react-icons/fa6";
import { useLocation } from "react-router-dom";

interface IModalInfoList {
  [key: string]: {
    header: string;
    title: string;
    text: string;
  };
}

interface ICashHistoryParams {
  page?: number;
  startDate?: string;
  endDate?: string;
  filters?: ICashFilters;
}

interface ICashFilters {
  locked: string;
  type: string;
  group: string;
  op: string;
  hash?: string;
}

const masks = new Masks();
export function General() {
  // Pagination Part
  const [page, setPage] = useState(1);
  const [quantity, setQuantity] = useState(10);
  const [order, setOrder] = useState("DESC");
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [locked, setLocked] = useState("a");
  const [type, setType] = useState("a");
  const [group, setGroup] = useState("a");
  const [op, setOp] = useState("a");
  const [hash, setHash] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [resultTime, setResultTime] = useState("Todo Período");

  // API Returns
  const [financialData, setFinancialData] = useState<ISummaryCashCustomerDTO>(
    {} as ISummaryCashCustomerDTO,
  );
  const [cashs, setCashs] = useState<ICashDTO[]>([]);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [show, setShow] = useState<{ filter: boolean }>({
    filter: false,
  });

  const [isLoading, setIsLoading] = useState({
    card: false,
    history: true,
  });

  const location = useLocation();
  const { changeHash } = useModalContext();
  const modalInfoList: IModalInfoList = {
    hashInfo: {
      header: "Hash de Transação",
      title: "Identificação Importante",
      text: "Este é o código interno de identificação de uma transação específica, através desta você pode localizar a transação com facilidade.",
    },
  };

  const [modalInfo, setModalInfo] = useState<IModalInfoDTO>({
    header: ``,
    title: ``,
    text: ``,
  });

  function getModalHashInfo(param: string, hash?: string) {
    const info = modalInfoList[param];
    if (!info) {
      pushToastify({
        message: `Houve um erro ao carregar as informações.`,
        type: "warning",
        theme: "dark",
      });
      return;
    }

    const text = `${info.text}${
      hash ? `<br><br>${addHashClickAndCopyHtml({ hash })}` : ``
    }`;

    setModalInfo({
      header: info.header,
      title: info.title,
      text,
    });

    OpenModal();
  }

  const OpenModal = () => {
    changeHash("modalInfo");
  };

  function getFilters(): ICashFilters {
    return {
      locked,
      type,
      group,
      op,
      hash: hash.trim() || undefined,
    };
  }

  function buildCashParams({
    page,
    startDate,
    endDate,
    filters = getFilters(),
  }: ICashHistoryParams) {
    return new URLSearchParams(
      Object.entries({
        ...(page && { page: page.toString() }),
        ...(page && { quantity: quantity.toString() }),
        ...(page && { order }),
        ...(filters.locked !== "a" && { locked: filters.locked }),
        ...(filters.type !== "a" && { type: filters.type }),
        ...(filters.group !== "a" && { group: filters.group }),
        ...(filters.op !== "a" && { op: filters.op }),
        ...(filters.hash && { hash: filters.hash }),
        ...(startDate && { startAt: startDate }),
        ...(endDate && { endAt: endDate }),
      }),
    ).toString();
  }

  async function getcashsummary(filters = getFilters()) {
    try {
      const params = buildCashParams({
        startDate,
        endDate,
        filters,
      });
      const result = await api.get(`/cash/transaction/summary?${params}`);

      setFinancialData(result.data);
      setIsLoading((prev) => ({ ...prev, card: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, card: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function getCashHistory({
    page,
    startDate,
    endDate,
    filters,
  }: ICashHistoryParams) {
    try {
      const params = buildCashParams({
        page,
        startDate,
        endDate,
        filters,
      });
      const result = await api.get(`/cash/transaction/history?${params}`);

      setCashs(result.data.list);
      
      setTotalPages(result.data.pages);
      setIsLoading((prev) => ({ ...prev, history: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, history: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function cleanFilters() {
    setType("a");
    setLocked("a");
    setGroup("a");
    setOp("a");
    setHash("");
    setStartDate("");
    setEndDate("");
    handleSubmit("", "", {
      locked: "a",
      type: "a",
      group: "a",
      op: "a",
      hash: undefined,
    });
  }

  function handleSubmit(
    _startDate: string,
    _endDate: string,
    filters = getFilters(),
  ) {
    setIsLoading({ card: true, history: true });
    setResultTime(
      _startDate.length + _endDate.length > 0
        ? `Período Selecionado`
        : `Todo Período`,
    );

    setPage(1);
    getcashsummary(filters);
    getCashHistory({
      page: 1,
      startDate: _startDate,
      endDate: _endDate,
      filters,
    });
  }

  const treatReleaseColumn = ({
    locked,
    releaseAt,
    queueData,
  }: {
    locked: boolean;
    releaseAt: string;
    queueData?: ICashRaffleTicketReleaseQueueDTO;
  }): any => {
    const date = releaseAt
      ? dayjs(releaseAt).format("DD/MM/YYYY [às] HH:mm")
      : "~";

    if (locked) {
      if (queueData) {
        if (
          queueData.releaseType ===
          ProfitTicketBalanceReleaseTypeEnum.RAFFLE_FINISHED
        ) {
          return (
            <>
              <div
                className="d-flex align-items-center"
                title={`Liberação após a finalização da Campanha`}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  window.open(
                    `/raffle/default/view?key=${queueData.raffleKey || ""}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                <div className="bg-info py-0 px-2 rounded">
                  <FaTicketAlt className="text-warning fs-4 me-1" />
                  <span className={`text-white`}>Condicionada</span>
                </div>
              </div>
            </>
          );
        }
      }

      return (
        <>
          <div
            className="d-flex align-items-center"
            title={`Liberação agendada`}
          >
            <FaClock className="text-warning fs-5 me-1" />
            <span className={``}>{date}</span>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="d-flex align-items-center" title={`Liberação aprovada`}>
          <FaCheckSquare className="text-success fs-5 me-1" />
          <span className={``}>{date}</span>
        </div>
      </>
    );
  };

  useEffect(() => {
    getCashHistory({
      page,
      startDate,
      endDate,
      filters: getFilters(),
    });
  }, [page]);

  useEffect(() => {
    getcashsummary(getFilters());
    getCashHistory({
      page,
      startDate,
      endDate,
      filters: getFilters(),
    });
  }, []);

  const balanceUnlocked = Number(financialData?.balances?.unlocked ?? 0);
  const balanceLocked = Number(financialData?.balances?.locked ?? 0);
  const balanceTotal = Number(
    financialData?.balances?.total ?? balanceUnlocked + balanceLocked,
  );

  return (
    <>
      {/* Filters */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div
              className="card-header"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShow((prev) => ({
                  ...prev,
                  filter: !show.filter,
                }));
              }}
            >
              <div className="d-flex justify-content-between">
                <div className="">
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para trazer um período específico.
                  </div>
                </div>
                <div className="">
                  {show.filter ? (
                    <FaMinus
                      className="fs-5"
                      title="Clique para ocultar as informações."
                    />
                  ) : (
                    <FaExpand
                      className="fs-5"
                      title="Clique para visualizar as informações."
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={`card-body ${show.filter ? `` : `d-none`}`}>
              <div className="row g-3 align-items-end">
                <div className="col-md-6 col-xl-4">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashHash"
                  >
                    Hash
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="cashHash"
                    value={hash}
                    placeholder="Hash da transação"
                    onChange={(e) => setHash(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSubmit(startDate, endDate);
                    }}
                  />
                </div>
                <div className="col-md-6 col-xl-4">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashGroup"
                  >
                    Grupo
                  </label>
                  <select
                    className="form-select"
                    aria-label="Grupo"
                    id="cashGroup"
                    value={group}
                    onChange={(e) => setGroup(e.target.value)}
                  >
                    <option value="a">Todos</option>
                    {cashGroups.map((cashGroup) => {
                      return (
                        <option key={cashGroup.id} value={cashGroup.id}>
                          {cashGroup.text}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-md-6 col-xl-4">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashOp"
                  >
                    Operação
                  </label>
                  <select
                    className="form-select"
                    aria-label="Operação"
                    id="cashOp"
                    value={op}
                    onChange={(e) => setOp(e.target.value)}
                  >
                    <option value="a">Todas</option>
                    {cashOps.map((cashOp) => {
                      return (
                        <option key={cashOp.id} value={cashOp.id}>
                          {cashOp.text}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-md-6 col-xl-2">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashType"
                  >
                    Tipo
                  </label>
                  <select
                    className="form-select"
                    aria-label="Tipo de Transação"
                    id="cashType"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="a">Todos</option>
                    {cashTypes.map((cashType) => {
                      return (
                        <option key={cashType.type} value={cashType.type}>
                          {cashType.text}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-md-6 col-xl-2">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashLocked"
                  >
                    Estado
                  </label>
                  <select
                    className="form-select"
                    aria-label="Estado da Transação"
                    id="cashLocked"
                    value={locked}
                    onChange={(e) => setLocked(e.target.value)}
                  >
                    <option value="a">Todos</option>
                    <option value="n">Liberado</option>
                    <option value="y">Bloqueado</option>
                  </select>
                </div>
                <div className="col-md-6 col-xl-2">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashStartDate"
                  >
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    onChange={(e) => setStartDate(e.target.value)}
                    id="cashStartDate"
                    value={startDate}
                  />
                </div>
                <div className="col-md-6 col-xl-2">
                  <label
                    className="form-label small fw-semibold mb-1"
                    htmlFor="cashEndDate"
                  >
                    Data Final
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    onChange={(e) => setEndDate(e.target.value)}
                    id="cashEndDate"
                    value={endDate}
                  />
                </div>
                <div className="col-md-12 col-xl-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success flex-fill"
                      onClick={() => handleSubmit(startDate, endDate)}
                    >
                      Filtrar
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={cleanFilters}
                      title="Limpar todos os filtros."
                    >
                      <FaBroom />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Financial Profit */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="card-title">Resumo Financeiro</div>
              <div className="card-category">
                Rendimentos e pendências resumidas.
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                {/* Total Income */}
                <div className="col-md-4">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Total de Entradas</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Total de entradas de clientes/vendas</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.card ? (
                      <div className="w-100">
                        <LoadingSkeleton count={2} />
                      </div>
                    ) : (
                      <div className="row d-flex justify-content-between">
                        <div className="col-md-12 col-xxl-6 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              financialData?.credits?.gross ?? 0,
                            )}
                          </span>
                          <br />
                          <span>Bruto</span>
                        </div>
                        <div className="col-md-12 col-xxl-6 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(financialData?.credits?.net) ??
                              0}
                          </span>
                          <br />
                          <span>Líquido</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total Outcome */}
                <div className="col-md-4">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Total de Saídas</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Saques, taxas, comissões, etc...</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.card ? (
                      <div className="w-100">
                        <LoadingSkeleton count={2} />
                      </div>
                    ) : (
                      <div className="row d-flex justify-content-between">
                        <div className="col-md-3 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.debits?.withdraw ?? 0),
                            )}
                          </span>
                          <br />
                          <span>Saques</span>
                        </div>
                        <div className="col-md-3 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.debits?.others ?? 0),
                            )}
                          </span>
                          <br />
                          <span>
                            Outros{" "}
                            <FaCircleInfo title="Trata-se de taxas de funcionamento, comissões, dentre outros custos." />
                          </span>
                        </div>
                        <div className="col-md-3 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.debits?.total ?? 0),
                            )}
                          </span>
                          <br />
                          <span>
                            Total{" "}
                            <FaCircleInfo title="Trata-se do total de saídas (saques, taxas, comissões, etc...)." />
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer's Balances | Profit */}
                <div className="col-md-4">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Balancete</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Rendimentos por status</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.card ? (
                      <div className="w-100">
                        <LoadingSkeleton count={2} />
                      </div>
                    ) : (
                      <div className="row d-flex justify-content-between g-3">
                        <div className="col-md-4 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(balanceUnlocked)}
                          </span>
                          <br />
                          <span>Liberado</span>
                          <div className="mt-3">
                            <Button
                              className="btn btn-outline-success px-3"
                              onClick={() => setIsWithdrawModalOpen(true)}
                            >
                              <FaMoneyBillTransfer className="me-2" />
                              Sacar
                            </Button>
                          </div>
                        </div>
                        <div className="col-md-4 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(balanceLocked)}
                          </span>
                          <br />
                          <span>À Liberar</span>
                        </div>
                        <div className="col-md-4 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(balanceTotal)}
                          </span>
                          <br />
                          <span>Total</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
              {resultTime}
            </span>
          </Card>
        </div>
      </div>

      {/* cashs Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                {/* Header of the Table */}
                <div className="col-md-8">
                  <h4 className="card-title">Transações</h4>
                  <p className="card-category">Lista de todas as transações</p>
                </div>
                {/* Ordenate Table */}
                <div className="col-md-4">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Quantidade de Registros por Página"
                          value={quantity}
                          onChange={(e) => setQuantity(Number(e.target.value))}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="75">75</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Ordenação da Tabela"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        >
                          <option value="ASC">Crescente</option>
                          <option value="DESC">Decrescente</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <button
                          className="btn btn-sm btn-success form-control px-2"
                          onClick={() => handleSubmit(startDate, endDate)}
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th scope="col" className="fs-6">
                            #
                          </th>
                          <th>Operação</th>
                          <th>Grupo</th>
                          <th>Tipo</th>
                          <th>Vl. Bruto</th>
                          <th>Vl. Líq.</th>
                          <th>Saldo</th>
                          <th>Liberação</th>
                          <th>Criado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading.history && (
                          <tr>
                            <td colSpan={10} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        )}

                        {cashs &&
                          cashs.length > 0 &&
                          cashs.map((cash) => {
                            const types = getCashTypes(cash.type);
                            const value = getCashFormattedValue(cash.type);
                            const opInfo = getCashOp(
                              cash.opData?.id || cash.opData?.name || cash.op,
                            );
                            const groupInfo = getCashGroup(
                              cash.groupData?.id ||
                                cash.groupData?.name ||
                                cash.opData?.group ||
                                opInfo.group,
                            );
                            const release = treatReleaseColumn({
                              locked: cash.locked,
                              releaseAt: cash.releaseAt,
                              queueData: cash?.cashRaffleTicketReleaseQueueData,
                            });
                            return (
                              <tr key={cash?.hash}>
                                <td
                                  onClick={() =>
                                    getModalHashInfo("hashInfo", cash?.hash)
                                  }
                                  title={cash.hash}
                                  style={{ cursor: "pointer" }}
                                >
                                  {cash?.hash
                                    ? `${(cash?.hash).slice(0, 5)}...`
                                    : "~"}
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${opInfo.color}`}
                                    title={opInfo.title}
                                  >
                                    {opInfo.text}
                                  </span>
                                </td>
                                <td>{groupInfo.text}</td>
                                <td>
                                  <span
                                    className={`badge bg-${types.color}`}
                                    title={types.text}
                                  >
                                    {types.title}
                                  </span>
                                </td>
                                <td>
                                  {masks?.brlCurrency(cash?.amountGross || 0)}
                                </td>
                                <td>
                                  <span className={`text-${value.color}`}>
                                    {value.sign}{" "}
                                    {masks?.brlCurrency(cash?.amount || 0)}
                                  </span>
                                </td>
                                <td>
                                  {masks?.brlCurrency(cash?.balance || 0)}
                                </td>
                                {/* Release */}
                                <td>{release}</td>
                                <td>
                                  {cash?.createdAt
                                    ? dayjs(cash?.createdAt).format(
                                        "DD/MM/YYYY [às] HH:mm:ss",
                                      )
                                    : "~"}
                                </td>
                              </tr>
                            );
                          })}

                        {isLoading.history ||
                          !cashs ||
                          (cashs?.length <= 0 && (
                            <tr>
                              <td colSpan={10} className="text-center">
                                Nenhum registro encontrado.
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>

      <ModalInfo
        header={modalInfo.header}
        title={modalInfo.title}
        text={modalInfo.text}
        isOpen={location?.hash === "#modalInfo"}
        onClose={() => {
          changeHash("");
        }}
      />

      {isWithdrawModalOpen && (
        <ModalPayoutRequest
          isOpen={isWithdrawModalOpen}
          onClose={() => setIsWithdrawModalOpen(false)}
          onCreated={() => {
            const filters = getFilters();

            getcashsummary(filters);
            getCashHistory({
              page,
              startDate,
              endDate,
              filters,
            });
          }}
        />
      )}
    </>
  );
}

